/**
 * Accessible DOM view of the complete authored scene.
 *
 * Native details/lists/forms keep browser keyboard semantics intact. The
 * controller owns no project data: it renders plain snapshots and emits
 * stable-ID commands through EventBus.
 */

import { crowdAreaDescription } from '../utils/crowdAreaDescription.js';
import { sceneOutlineKey } from '../utils/sceneSemantics.js';
import { groupChoiceHint, GROUP_HINT, GROUP_FIELD_HINTS } from '../utils/groupSettings.js';
import { WAITING_HINT, WAITING_FIELD_HINT, WAITING_RELEASE_HINTS } from '../utils/waitingSettings.js';
import { IMAGE_COORDINATES, CROWD_ENTRY_WEIGHT } from '../config/constants.js';
import { formatCrowdDotSize, shiftDecimal } from '../utils/uiReadouts.js';
import { createCrowdArea } from '../utils/crowdArea.js';
import { entryWeightHint, entryWeightValue } from '../utils/entryWeight.js';

const OUTLINE_CANONICAL_VALUE = Symbol('outlineCanonicalValue');
// A field the author has typed in or chosen in (UI-06 W-02, Codex r1): it
// submits what it holds, though that is the value it was drawn with.
const OUTLINE_EDITED = Symbol('outlineEdited');
const OUTLINE_DRAFT_CONTEXT = Symbol('outlineDraftContext');
const OUTLINE_ERROR_ID_PREFIX = 'scene-outline-error-';
// A field's reason for being off (CROWD-07 F15): unique per drawing, never looked up by id.
const OUTLINE_REASON_ID_PREFIX = 'scene-outline-reason-';
let outlineReasonSerial = 0;

// Waypoints and polygon vertices may sit off the image (DEF-03), as far as a
// project can store a point, so their position fields take that range; 0–100%
// is the image itself (DEF-35). Network nodes and bend points are authored on
// the image and keep 0–100.
const IMAGE_POSITION_PERCENT = Object.freeze({
  min: IMAGE_COORDINATES.MIN * 100,
  max: IMAGE_COORDINATES.MAX * 100,
});

/**
 * A bend point's position field: 0–100%, and the value it holds, both as
 * stored and as the field shows it. A traced bend sits where its minor
 * waypoint is, which may be off the image (DEF-41); left as it is, the app
 * keeps it, and a new value is held to the image. The field shows two
 * decimals (UI-06 W-02), -18.92 for -18.916, so bounds from the stored value
 * alone refused the field as shown, and the whole form with it (Codex r1).
 * @param {number} value - The stored position, in percent
 * @param {number} [shown] - The value the field shows
 */
function bendPercentRange(value, shown = value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return { min: 0, max: 100 };
  const label = Number.isFinite(Number(shown)) ? Number(shown) : number;
  return { min: Math.min(0, number, label), max: Math.max(100, number, label) };
}

/**
 * Each field's hint (UI-03), in the outline's own words. ParamTooltip wires a
 * label's `data-tip` as its field's description as the form is drawn, and
 * shows it on hover, click and keyboard focus, as in the inspector.
 */
const FIELD_HINTS = Object.freeze({
  // Waypoints and polygon vertices may sit off the image; nodes and bend
  // points are placed on it (see IMAGE_POSITION_PERCENT above).
  imageX: '0% is the image’s left edge, 100% its right edge; beyond them is off the image',
  imageY: '0% is the image’s top edge, 100% its bottom edge; beyond them is off the image',
  onImageX: '0% is the image’s left edge, 100% its right edge',
  onImageY: '0% is the image’s top edge, 100% its bottom edge',
  newWaypointX: 'Where the new waypoint goes: 0% is the image’s left edge, 100% its right edge; ' +
    'beyond them is off the image',
  newWaypointY: 'Where the new waypoint goes: 0% is the image’s top edge, 100% its bottom edge; ' +
    'beyond them is off the image',
  newVertexX: 'Where the new corner goes, after the last one: 0% is the image’s left edge, 100% its right ' +
    'edge; beyond them is off the image',
  newVertexY: 'Where the new corner goes, after the last one: 0% is the image’s top edge, 100% its bottom ' +
    'edge; beyond them is off the image',
  newNodeX: 'Where the new node goes: 0% is the image’s left edge, 100% its right edge',
  newNodeY: 'Where the new node goes: 0% is the image’s top edge, 100% its bottom edge',
  newBendX: 'Where the new bend goes, after the last one: 0% is the image’s left edge, 100% its right edge',
  newBendY: 'Where the new bend goes, after the last one: 0% is the image’s top edge, 100% its bottom edge',
  insertPosition: 'Where the new waypoint goes in the route order: at the start, or after the waypoint chosen',
  waypointType: 'Major waypoints carry a wait and a leg speed; minor waypoints only shape the ' +
    'route between them',
  wait: 'How long the path head waits at this waypoint before moving on',
  legSpeed: 'How fast the path head travels the leg after this waypoint: 2 takes half the time, ' +
    '0.5 twice as long',
  fadeIn: 'How long the area takes to fade in when it appears',
  fadeOut: 'How long the area takes to fade out when it disappears',
  crowdName: 'The crowd’s name in Layers and in this outline',
  crowdVisibility: 'Hidden crowds are not drawn, in the editor or in exports; their settings are kept',
  guide: 'What the dots travel along: the route, or a custom network of nodes and paths',
  dots: 'How many dots this crowd releases in all',
  releaseStart: 'When the first dots set off, as a percentage of the timeline',
  releaseLength: 'How much of the timeline, as a percentage, the release is spread across',
  releaseTiming: 'How unevenly dots set off: 0% evenly spaced, 100% at random. Release bias and Busyness ' +
    'over time then shift set-offs, so 0% is even only with bias 0 and even busyness',
  // The bias bends set-offs towards the start or the end of the release
  // (dotOnsetFraction in src/utils/crowdArrival.js), said as a direction and
  // an amount, as the card's readout says it (CROWD-07 F15).
  releaseBias: 'Earlier sets more dots off near the start of the release; Later near its end; Even favours neither',
  releaseBiasAmount: 'How far the release leans towards the chosen end: 0% not at all, 100% the most',
  speed: 'How fast the dots travel, in image lengths per second: at 1, a dot covers the image’s width across ' +
    'or its height down in about a second; Pace variation makes some faster and some slower',
  paceVariation: 'How much each dot’s pace differs: 0% moves every dot at the same speed',
  walkingVariation: 'Sideways walking variation as dots travel: 0% follows the line exactly',
  dotColour: 'A hex colour such as #56B4E9, or transparent to hide the dots',
  // The Motion card's "At journey end" hint (CROWD-07 F6), word for word: one
  // description for one control (tests/uiStrings.test.js pins the two equal).
  journeyEnd: "Disappear removes a dot at its journey's end; Collect at the end parks it there; Respawn at the " +
    'start sends a new dot from the start with its own pace and walking variation; Repeat journey follows the ' +
    'same paths again at the same pace',
  nodeType: 'Entry nodes release dots and exit nodes end their journeys; pass-through nodes pass them on, or ' +
    'end them where no path leads out. With no exit, journeys end at nodes with one path; with no ' +
    'entry, dots set off from any node they can leave',
  nodeLabel: 'An optional name, shown after the node’s number in this outline',
  sourceNode: 'The node the new path leaves; a one-way path carries dots only away from it',
  destinationNode: 'The node the new path leads to',
  newEdgeDirection: 'Two-way paths carry dots both ways; one-way paths only from Source node to Destination node',
  edgeDirection: 'Two-way paths carry dots both ways; one-way paths only from the first node in the path’s ' +
    'name to the second',
  pathWeight: 'How strongly dots prefer this path at a node where paths meet: between the paths a dot can take, ' +
    'weight 2 is twice as likely to be picked as weight 1. Arriving dots avoid turning straight back when ' +
    'another path is available',
});

/**
 * Dot size's hint (CROWD-07 F1). The field edits the model's own unit (×); the
 * card's Size reads the diameter drawn, so the hint gives this value's.
 * @param {number} dotSize
 * @returns {string}
 */
function dotSizeHint(dotSize) {
  return `Dot diameter as a multiple of ${formatCrowdDotSize(1)}: ${dotSize} draws a dot `
    + `${formatCrowdDotSize(dotSize)} across; exports scale it from the project’s reference short edge`;
}

/**
 * A Release bias as its direction and amount (CROWD-07 F15), as the card's
 * readout says it, to the stored value's own precision: "Earlier 37.5%".
 * @param {number} percent - The bias in percent, −100…100
 * @returns {string}
 */
function releaseBiasText(percent) {
  if (!(percent < 0) && !(percent > 0)) return 'Even';
  return percent < 0 ? `Earlier ${-percent}%` : `Later ${percent}%`;
}

/**
 * The Even direction's reason for the amount being off [Q9]: "share" is the
 * Path card's word, so the lead's settlement says it in the bias's own
 * (glossary, one word per concept).
 */
const RELEASE_BIAS_EVEN_REASON = 'Even favours neither end.';

/** What a dot does at its journey's end, as the Motion card says it (UI-06 J-02). */
const JOURNEY_END_LABELS = Object.freeze({
  respawn: 'Respawn at the start',
  loop: 'Repeat journey',
  disappear: 'Disappear',
  collect: 'Collect at the end',
});

function el(tag, { className = '', text = '', attrs = {}, data = {} } = {}) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== '') node.textContent = String(text);
  for (const [name, value] of Object.entries(attrs)) {
    if (value === false || value == null) continue;
    if (value === true) node.setAttribute(name, '');
    else node.setAttribute(name, String(value));
  }
  for (const [name, value] of Object.entries(data)) {
    if (value != null) node.dataset[name] = String(value);
  }
  return node;
}

function plural(count, one, many = `${one}s`) {
  return `${count} ${count === 1 ? one : many}`;
}

function button(label, action, data = {}, { danger = false, disabled = false, key = null } = {}) {
  return el('button', {
    className: `btn btn-secondary scene-outline-action${danger ? ' scene-outline-danger' : ''}`,
    text: label,
    attrs: { type: 'button', disabled },
    data: { outlineAction: action, outlineKey: key, ...data },
  });
}

function labelledInput(name, label, value, {
  type = 'number', min = null, max = null, step = null, required = true,
  key = null, inputMode = 'decimal', readOnly = false,
  canonicalValue = undefined, maxLength = null, tip = null,
} = {}) {
  const wrapper = el('label', { className: 'scene-outline-field' });
  wrapper.appendChild(el('span', { text: label, attrs: { 'data-tip': tip } }));
  const input = el('input', {
    attrs: {
      name, type, min, max, step, required, inputmode: inputMode,
      readonly: readOnly, maxlength: maxLength,
    },
    data: { outlineKey: key },
  });
  if (canonicalValue !== undefined) input[OUTLINE_CANONICAL_VALUE] = canonicalValue;
  input.value = String(value);
  // Text inputs normalise CR/LF while assigning `.value`. Treat that actual
  // control value as the unchanged display form, while retaining the raw
  // canonical value separately for lossless combined-form updates.
  input.defaultValue = input.value;
  wrapper.appendChild(input);
  return wrapper;
}

function labelledSelect(name, label, value, options, { key = null, tip = null } = {}) {
  const wrapper = el('label', { className: 'scene-outline-field' });
  wrapper.appendChild(el('span', { text: label, attrs: { 'data-tip': tip } }));
  const select = el('select', { attrs: { name }, data: { outlineKey: key } });
  for (const option of options) {
    const item = typeof option === 'string' ? { value: option, label: option } : option;
    const optionEl = el('option', {
      text: item.label,
      attrs: { value: item.value, disabled: item.disabled },
    });
    if (String(item.value) === String(value)) {
      optionEl.selected = true;
      optionEl.defaultSelected = true;
    }
    select.appendChild(optionEl);
  }
  wrapper.appendChild(select);
  return wrapper;
}

/**
 * Release bias as a direction and an amount (CROWD-07 F15), not the signed
 * number it is stored as: each submits the value it was drawn from until it
 * is edited, so a stored −0.375 survives an Apply. The amount's reason line,
 * a description outside its label, says why it is off while Even is chosen
 * (`syncReleaseBias`).
 * @param {Object} emitter - The emitter's outline descriptor
 * @returns {HTMLElement[]}
 */
function releaseBiasFields(emitter) {
  const canonical = Number(emitter.intensityRampCanonical);
  const direction = canonical < 0 ? 'earlier' : (canonical > 0 ? 'later' : 'even');
  const amount = labelledInput('intensityRampAmount', 'Release bias amount (%)', Math.abs(emitter.intensityRamp), {
    min: 0, max: 100, step: 'any', key: `${emitter.key}:ramp-amount`,
    canonicalValue: Math.abs(canonical),
    tip: groupChoiceHint(emitter.groupsActive ? 'group' : null, FIELD_HINTS.releaseBiasAmount),
  });
  outlineReasonSerial += 1;
  const reason = el('p', {
    className: 'scene-outline-hint scene-outline-reason',
    attrs: { id: `${OUTLINE_REASON_ID_PREFIX}${outlineReasonSerial}`, hidden: true },
  });
  return [
    labelledSelect('intensityRampDirection', 'Release bias', direction, [
      { value: 'earlier', label: 'Earlier' },
      { value: 'even', label: 'Even' },
      { value: 'later', label: 'Later' },
    ], { key: `${emitter.key}:ramp-direction`,
      tip: emitter.waitingActive ? WAITING_RELEASE_HINTS.bias : FIELD_HINTS.releaseBias }),
    amount,
    reason,
  ];
}

/**
 * The amount follows the direction [Q9]: Even is 0, so the amount is set to
 * 0 and turned off, its reason shown and named first among what describes
 * it; another direction turns it back on, with no reason. Run as a form is
 * drawn (after its drafts are put back), as its direction changes, and on
 * Escape.
 * @param {HTMLFormElement} formEl
 */
function syncReleaseBias(formEl) {
  const direction = formEl?.elements?.intensityRampDirection;
  const amount = formEl?.elements?.intensityRampAmount;
  const reason = amount?.closest('label')?.nextElementSibling;
  if (!direction || !amount || !reason?.classList.contains('scene-outline-reason')) return;
  const even = direction.value === 'even';
  if (even) amount.value = '0';
  amount.disabled = even;
  reason.textContent = even ? RELEASE_BIAS_EVEN_REASON : '';
  reason.hidden = !even;
  const tokens = (amount.getAttribute('aria-describedby') || '').split(/\s+/).filter(id => id && id !== reason.id);
  if (even) tokens.push(reason.id);
  if (tokens.length) amount.setAttribute('aria-describedby', tokens.join(' '));
  else amount.removeAttribute('aria-describedby');
}

/** Shape drafts reveal their dimensions before Apply; the app alone changes the node. */
function syncNodeArea(formEl) {
  if (formEl?.dataset?.outlineAction !== 'update-node-area') return;
  const inactive = formEl.dataset.areaInactive === 'true';
  const enabled = formEl.elements.shape.value !== 'none';
  for (const key of ['width', 'height', 'turn']) {
    const field = formEl.elements[key];
    field.disabled = inactive || !enabled;
    field.closest('label').hidden = !enabled;
  }
  formEl.querySelector('details').hidden = !enabled;
  formEl.elements.shape.disabled = inactive;
  formEl.querySelector('[type="submit"]').disabled = inactive;
}

function form(action, data, fields, submitLabel = 'Apply', key = null, draftContext = null) {
  const node = el('form', {
    className: 'scene-outline-form',
    data: { outlineAction: action, outlineFormKey: key, ...data },
  });
  if (draftContext !== null) node[OUTLINE_DRAFT_CONTEXT] = draftContext;
  const grid = el('div', { className: 'scene-outline-field-grid' });
  fields.forEach(field => grid.appendChild(field));
  node.appendChild(grid);
  node.appendChild(el('button', {
    className: 'btn btn-primary scene-outline-submit',
    text: submitLabel,
    attrs: { type: 'submit' },
    data: { outlineKey: key },
  }));
  return node;
}

function detailsBlock(key, summaryText, open, className = '') {
  const details = el('details', {
    className: `scene-outline-details ${className}`.trim(),
    data: { outlineDisclosure: key },
  });
  details.open = open;
  details.appendChild(el('summary', {
    className: 'scene-outline-summary',
    text: summaryText,
    data: { outlineKey: `${key}:summary` },
  }));
  return details;
}

function itemFor(details) {
  const item = el('li', { className: 'scene-outline-item' });
  item.appendChild(details);
  return item;
}

function description(text) {
  return el('p', { className: 'scene-outline-hint', text });
}

function readOnlyList(entries) {
  const list = el('dl', { className: 'scene-outline-readonly' });
  for (const [term, value] of entries) {
    list.append(el('dt', { text: term }), el('dd', { text: value }));
  }
  return list;
}

function areaContainsKey(area, key) {
  return key === area.key || area.points.some(point => key === point.key);
}

function waypointContainsKey(waypoint, key) {
  return key === waypoint.key || areaContainsKey(waypoint.area, key);
}

function edgeContainsKey(edge, key) {
  return key === edge.key || edge.controlPoints.some(point => key === point.key);
}

function networkContainsKey(graph, key) {
  return key === graph.key
    || graph.nodes.some(node => key === node.key)
    || graph.edges.some(edge => edgeContainsKey(edge, key));
}

function crowdContainsKey(crowd, key) {
  return key === crowd.key
    || crowd.emitters.some(emitter => key === emitter.key)
    || networkContainsKey(crowd.graph, key);
}

export class SceneOutlineController {
  constructor(container, eventBus) {
    this.container = container;
    this.eventBus = eventBus;
    this._open = new Map();
    this._snapshot = null;
    // The selection last revealed, and whether the render under way reveals
    // a new one (set as each render starts).
    this._revealedSelection = null;
    this._revealing = false;
    this._error = null;
    this._errorSerial = 0;
    this._duplicateFeedback = new Map();
    this._duplicateErrors = new Map();
    this._duplicateFeedbackSerial = 0;
    this._drafts = new Map();

    this._unsubscribes = [
      this.eventBus.on('scene-outline:update', snapshot => this.render(snapshot)),
      this.eventBus.on('scene-outline:error', error => this._showCommandError(error)),
      this.eventBus.on('scene-outline:accepted', result => this._acceptCommand(result)),
      this.eventBus.on('project:replaced', () => this._resetDrafts()),
      this.eventBus.on('app:cleared', () => this._resetDrafts()),
    ];
    this._boundSubmit = event => this._onSubmit(event);
    this._boundClick = event => this._onClick(event);
    this._boundKeyDown = event => this._onKeyDown(event);
    this._boundEdit = event => this._onEdit(event);
    this.container?.addEventListener('submit', this._boundSubmit);
    this.container?.addEventListener('click', this._boundClick);
    this.container?.addEventListener('keydown', this._boundKeyDown);
    this.container?.addEventListener('input', this._boundEdit);
    this.container?.addEventListener('change', this._boundEdit);
  }

  destroy() {
    this._unsubscribes.forEach(unsubscribe => unsubscribe?.());
    this.container?.removeEventListener('submit', this._boundSubmit);
    this.container?.removeEventListener('click', this._boundClick);
    this.container?.removeEventListener('keydown', this._boundKeyDown);
    this.container?.removeEventListener('input', this._boundEdit);
    this.container?.removeEventListener('change', this._boundEdit);
    this._drafts.clear();
  }

  _rememberOpenState() {
    for (const details of this.container.querySelectorAll('details[data-outline-disclosure]')) {
      this._open.set(details.dataset.outlineDisclosure, details.open);
    }
  }

  _isOpen(key, defaultOpen = false, selected = false) {
    // A new selection reveals its target, even inside a branch the author
    // collapsed. After that the author's own opening and closing win, so an
    // entry holding the selection closes like any other (DEF-40).
    if (selected && this._revealing) return true;
    return this._open.has(key) ? this._open.get(key) : defaultOpen;
  }

  _focusedKey() {
    const active = document.activeElement;
    return this.container?.contains(active) ? active?.dataset?.outlineKey || null : null;
  }

  _findKey(key) {
    if (!key) return null;
    return [...this.container.querySelectorAll('[data-outline-key]')]
      .find(node => node.dataset.outlineKey === key) || null;
  }

  _openFocusAncestry(snapshot) {
    const focus = snapshot?.focusKey;
    if (!focus) return;
    const within = key => focus === key || focus.startsWith(`${key}:`);
    const open = key => this._open.set(key, true);

    if (within('route')) open('route');
    for (const waypoint of snapshot.route) {
      const area = waypoint.area;
      const point = area.points.find(item => within(item.key));
      if (!within(waypoint.key) && !within(area.key) && !point) continue;
      open('route');
      open(waypoint.key);
      if (within(area.key) || point) open(area.key);
      if (point) open(point.key);
    }

    if (within('crowds')) open('crowds');
    for (const crowd of snapshot.crowds) {
      const emitter = crowd.emitters.find(item => within(item.key));
      const node = crowd.graph.nodes.find(item => within(item.key));
      const edge = crowd.graph.edges.find(item =>
        within(item.key) || item.controlPoints.some(point => within(point.key))
      );
      const control = edge?.controlPoints.find(point => within(point.key));
      const relevant = within(crowd.key) || emitter || within(crowd.graph.key) || node || edge;
      if (!relevant) continue;
      open('crowds');
      open(crowd.key);
      if (emitter) {
        open(sceneOutlineKey('emitters', crowd.id));
        open(emitter.key);
      }
      if (within(crowd.graph.key) || node || edge) open(crowd.graph.key);
      if (node) {
        open(sceneOutlineKey('nodes', crowd.id));
        open(node.key);
      }
      if (edge) {
        open(sceneOutlineKey('edges', crowd.id));
        open(edge.key);
      }
      if (control) {
        open(sceneOutlineKey('controls', crowd.id, edge.id));
        open(control.key);
      }
    }
  }

  /**
   * @param {Object} snapshot
   * @param {string|null} [disclosureFocus] - The summary an author just
   *   opened or closed, which keeps focus though focus was elsewhere
   */
  render(snapshot, disclosureFocus = null) {
    if (!this.container || !snapshot) return;
    const priorFocus = this._focusedKey();
    this._captureDirtyDrafts();
    this._pruneDrafts(snapshot);
    this._rememberOpenState();
    // A snapshot's selection is revealed, and its focus request met, once: a
    // redraw of the same snapshot replays neither, or the entries they opened
    // could never close and focus would leave what the author clicked (DEF-40).
    const selection = snapshot.selectionKey ?? null;
    this._revealing = selection !== this._revealedSelection;
    this._revealedSelection = selection;
    this._snapshot = snapshot.focusKey ? { ...snapshot, focusKey: null } : snapshot;
    this._openFocusAncestry(snapshot);

    const fragment = document.createDocumentFragment();
    fragment.append(this._renderRoute(snapshot), this._renderCrowds(snapshot));
    this.container.replaceChildren(fragment);
    this._restoreDirtyDrafts();
    for (const formEl of this.container.querySelectorAll('form[data-outline-action="update-emitter"]')) {
      syncReleaseBias(formEl);
    }
    for (const formEl of this.container.querySelectorAll('form[data-outline-action="update-node-area"]')) {
      syncNodeArea(formEl);
    }
    this._renderCommandError();
    this._renderDuplicateErrors();

    const requested = disclosureFocus || snapshot.focusKey || priorFocus;
    if (requested) {
      queueMicrotask(() => {
        const target = this._findKey(requested)
          || this._findKey(snapshot.selectionKey ? `${snapshot.selectionKey}:select` : null)
          || this._findKey('route:summary');
        let ancestor = target?.closest('details[data-outline-disclosure]');
        // A summary shows while its own entry is closed, so only the entries
        // around it are opened: opening its own reopened an entry the author
        // had just closed (DEF-40).
        if (ancestor && target.parentElement === ancestor && target.matches('summary')) {
          ancestor = ancestor.parentElement?.closest('details[data-outline-disclosure]');
        }
        while (ancestor) {
          ancestor.open = true;
          this._open.set(ancestor.dataset.outlineDisclosure, true);
          ancestor = ancestor.parentElement?.closest('details[data-outline-disclosure]');
        }
        target?.focus();
      });
    }
  }

  _selected(key) {
    return this._snapshot?.selectionKey === key;
  }

  _captureDirtyDrafts() {
    for (const formEl of this.container.querySelectorAll('form[data-outline-form-key]')) {
      const fields = new Map();
      for (const [index, field] of [...formEl.elements].entries()) {
        if (!field.name || field.disabled) continue;
        const fieldKey = field.dataset?.outlineKey || `${field.name}:${index}`;
        const checkable = field.type === 'checkbox' || field.type === 'radio';
        const dirty = checkable
          ? field.checked !== field.defaultChecked
          : field.value !== this._defaultFieldValue(field);
        // An edited field is kept though it holds its default: the edit is.
        const edited = field[OUTLINE_EDITED] === true;
        if (!dirty && !edited) continue;
        fields.set(fieldKey, {
          name: field.name,
          value: field.value,
          checked: checkable ? field.checked : null,
          edited,
        });
      }
      const formKey = formEl.dataset.outlineFormKey;
      if (fields.size) {
        this._drafts.set(formKey, {
          fields,
          context: formEl[OUTLINE_DRAFT_CONTEXT] ?? null,
        });
      }
      else this._drafts.delete(formKey);
    }
  }

  _restoreDirtyDrafts() {
    for (const [formKey, draft] of this._drafts) {
      const formEl = this._formForKey(formKey);
      if (!formEl) continue;
      if (draft.context !== null && formEl[OUTLINE_DRAFT_CONTEXT] !== draft.context) {
        this._drafts.delete(formKey);
        continue;
      }
      for (const [fieldKey, fieldDraft] of draft.fields) {
        const field = [...formEl.elements].find((candidate, index) =>
          candidate.dataset?.outlineKey === fieldKey
          || (!candidate.dataset?.outlineKey && `${candidate.name}:${index}` === fieldKey)
        );
        if (!field || field.name !== fieldDraft.name || field.disabled) continue;
        if (fieldDraft.checked !== null) field.checked = fieldDraft.checked;
        else if (field.tagName !== 'SELECT'
            || [...field.options].some(option => option.value === fieldDraft.value)) {
          field.value = fieldDraft.value;
        }
        if (fieldDraft.edited) field[OUTLINE_EDITED] = true;
      }
    }
  }

  _pruneDrafts(snapshot) {
    const valid = new Set(['route:add-submit']);
    for (const waypoint of snapshot.route) {
      valid.add(`${waypoint.key}:apply`);
      if (waypoint.area.shape !== 'polygon') continue;
      valid.add(`${waypoint.area.key}:timing`);
      valid.add(`${waypoint.area.key}:add`);
      for (const point of waypoint.area.points) valid.add(`${point.key}:apply`);
    }
    for (const crowd of snapshot.crowds) {
      valid.add(`${crowd.key}:apply`);
      for (const emitter of crowd.emitters) {
        if (emitter.primary) valid.add(`${emitter.key}:apply`);
        valid.add(`${emitter.key}:groups-apply`);
        valid.add(`${emitter.key}:waiting-apply`);
      }
      valid.add(`${crowd.graph.key}:add-node`);
      if (crowd.graph.nodes.length >= 2) valid.add(`${crowd.graph.key}:connect`);
      for (const node of crowd.graph.nodes) {
        valid.add(`${node.key}:apply`);
        if (['entry', 'exit'].includes(node.type) || node.area) valid.add(`${node.key}:area-apply`);
        if (node.type === 'entry' || node.entryWeight !== undefined) valid.add(`${node.key}:entry-weight-apply`);
      }
      for (const edge of crowd.graph.edges) {
        valid.add(`${edge.key}:apply`);
        valid.add(`${edge.key}:add-control`);
        for (const point of edge.controlPoints) valid.add(`${point.key}:apply`);
      }
    }
    for (const formKey of this._drafts.keys()) {
      if (!valid.has(formKey)) this._drafts.delete(formKey);
    }
  }

  _defaultFieldValue(field) {
    if (field.tagName !== 'SELECT') return field.defaultValue;
    return [...field.options].find(option => option.defaultSelected)?.value
      ?? field.options[0]?.value
      ?? '';
  }

  _formForKey(key) {
    if (!key) return null;
    return [...this.container.querySelectorAll('form[data-outline-form-key]')]
      .find(formEl => formEl.dataset.outlineFormKey === key) || null;
  }

  _clearCommandError() {
    this._error = null;
    const errors = this.container.querySelectorAll('.scene-outline-error:not([data-outline-scoped-error])');
    for (const error of errors) error.remove();
    for (const field of this.container.querySelectorAll('[aria-invalid="true"]')) {
      field.removeAttribute('aria-invalid');
      // Only the error's token goes: the field's hint keeps describing it.
      const kept = (field.getAttribute('aria-describedby') || '').split(/\s+/)
        .filter(id => id && !id.startsWith(OUTLINE_ERROR_ID_PREFIX));
      if (kept.length) field.setAttribute('aria-describedby', kept.join(' '));
      else field.removeAttribute('aria-describedby');
    }
  }

  /** A form's fields back to unedited: they submit their stored values again. */
  _clearEdits(formEl) {
    for (const field of formEl?.elements ?? []) delete field[OUTLINE_EDITED];
  }

  _resetDrafts() {
    this._drafts.clear();
    for (const key of this._duplicateErrors.keys()) this._clearDuplicateError(key);
    this._duplicateFeedback.clear();
    for (const formEl of this.container.querySelectorAll('form[data-outline-form-key]')) {
      formEl.reset();
      this._clearEdits(formEl);
    }
    this._clearCommandError();
  }

  _showCommandError(error = {}) {
    if (error.scoped) {
      if (!this._currentDuplicateFeedback(error)) return;
      this._clearDuplicateError(error.formKey);
      this._duplicateErrors.set(error.formKey, { message: error.message, context: error.context });
      this._renderDuplicateErrors();
      const active = document.activeElement;
      if (!active || active === document.body || !active.isConnected) {
        this._formForKey(error.formKey)?.querySelector('[type="submit"]')?.focus();
      }
      return;
    }
    if (!error.formKey || !error.message) return;
    if (error.formScoped) this._clearCommandError();
    const formKey = String(error.formKey);
    this._error = {
      formKey,
      message: String(error.message),
      context: this._formForKey(formKey)?.[OUTLINE_DRAFT_CONTEXT] ?? null,
    };
    this._renderCommandError();
  }

  _acceptCommand(result = {}) {
    const { formKey } = result;
    if (result.scoped) {
      if (result.clearContext) {
        if (this._duplicateErrors.get(formKey)?.context === result.clearContext) this._clearDuplicateError(formKey);
      } else if (this._currentDuplicateFeedback(result)) this._clearDuplicateError(formKey);
      return;
    }
    if (!formKey) return;
    const normalizedKey = String(formKey);
    const formEl = this._formForKey(normalizedKey);
    if (formEl) {
      for (const field of formEl.elements) {
        if (!field.name || field.disabled) continue;
        if (field.type === 'checkbox' || field.type === 'radio') {
          field.defaultChecked = field.checked;
        } else if (field.tagName === 'SELECT') {
          for (const option of field.options) option.defaultSelected = option.selected;
        } else {
          field.defaultValue = field.value;
        }
      }
      this._clearEdits(formEl);
    }
    this._drafts.delete(normalizedKey);
    if (!result.formScoped || this._error?.formKey === normalizedKey) this._clearCommandError();
  }

  _currentDuplicateFeedback(result) {
    const pending = this._duplicateFeedback.get(result.formKey);
    return Boolean(pending) && pending.token === result.feedbackToken && pending.context === result.context &&
      this._snapshot?.crowds.some(crowd => crowd.duplicateContext === result.context);
  }

  _clearDuplicateError(key) {
    const formEl = this._formForKey(key);
    const error = formEl?.querySelector('[data-outline-scoped-error]');
    if (error) {
      const submit = formEl.querySelector('[type="submit"]');
      const kept = (submit.getAttribute('aria-describedby') || '').split(/\s+/).filter(id => id && id !== error.id);
      if (kept.length) submit.setAttribute('aria-describedby', kept.join(' '));
      else submit.removeAttribute('aria-describedby');
      error.remove();
    }
    this._duplicateErrors.delete(key);
  }

  _renderDuplicateErrors() {
    for (const [key, stored] of this._duplicateErrors) {
      if (!this._snapshot?.crowds.some(crowd => crowd.duplicateContext === stored.context)) {
        this._clearDuplicateError(key); continue;
      }
      const formEl = this._formForKey(key);
      if (!formEl || formEl[OUTLINE_DRAFT_CONTEXT] !== stored.context ||
          formEl.querySelector('[data-outline-scoped-error]')) continue;
      const id = `${OUTLINE_ERROR_ID_PREFIX}${++this._errorSerial}`;
      formEl.appendChild(el('p', { className: 'scene-outline-error', text: stored.message,
        attrs: { id, 'data-outline-scoped-error': 'true' } }));
      const submit = formEl.querySelector('[type="submit"]');
      submit.setAttribute('aria-describedby', [id, submit.getAttribute('aria-describedby')].filter(Boolean).join(' '));
    }
  }

  _renderCommandError() {
    if (!this._error) return;
    const formEl = this._formForKey(this._error.formKey);
    let context = formEl?.[OUTLINE_DRAFT_CONTEXT];
    // Lazy disclosures omit the form; its live emitter context still owns the refusal.
    const suffix = this._error.formKey.endsWith(':groups-apply') ? 'groups'
      : this._error.formKey.endsWith(':waiting-apply') ? 'waiting' : null;
    if (!formEl && suffix) {
      for (const crowd of this._snapshot?.crowds || []) {
        const emitter = crowd.emitters.find(item => `${item.key}:${suffix}-apply` === this._error.formKey);
        if (emitter) { context = emitter[`${suffix}Context`]; break; }
      }
    }
    if (this._error.context !== null && context !== this._error.context) {
      this._clearCommandError();
      return;
    }
    if (!formEl || formEl.querySelector('.scene-outline-error')) return;
    const errorId = `${OUTLINE_ERROR_ID_PREFIX}${++this._errorSerial}`;
    const error = el('p', {
      className: 'scene-outline-error',
      text: this._error.message,
      attrs: { id: errorId, role: 'alert' },
    });
    for (const field of formEl.querySelectorAll('input, select, textarea')) {
      field.setAttribute('aria-invalid', 'true');
      // Ahead of the field's hint, which it keeps: the error is announced first.
      const others = (field.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
      field.setAttribute('aria-describedby', [errorId, ...others].join(' '));
    }
    formEl.appendChild(error);
  }

  _selectButton(label, kind, key, data) {
    const selected = this._selected(key);
    const select = button(label, 'select', { kind, ...data }, { key: `${key}:select` });
    select.setAttribute('aria-pressed', selected ? 'true' : 'false');
    if (selected) select.classList.add('is-selected');
    return select;
  }

  _renderRoute(snapshot) {
    const selectedWaypoint = snapshot.route.find(waypoint =>
      waypointContainsKey(waypoint, snapshot.selectionKey)
    );
    const defaultAfterWaypointId = selectedWaypoint?.id
      ?? snapshot.route[snapshot.route.length - 1]?.id
      ?? '';
    const group = detailsBlock(
      'route',
      `Route — ${plural(snapshot.majorCount, 'major waypoint')}, ${plural(snapshot.minorCount, 'minor waypoint')}`,
      this._isOpen(
        'route',
        true,
        snapshot.route.some(waypoint => waypointContainsKey(waypoint, snapshot.selectionKey))
      ),
      'scene-outline-group'
    );
    if (!group.open) return group;
    const content = el('div', { className: 'scene-outline-content' });
    content.appendChild(description(
      'Route order lists major waypoints, which carry the timing, and minor waypoints, which only shape ' +
      'the route. Positions are percentages of the image.'
    ));

    content.appendChild(form('add-waypoint', {}, [
      labelledSelect('afterWaypointId', 'Insert position', defaultAfterWaypointId, [
        { value: '', label: 'Start of route' },
        ...snapshot.route.map(waypoint => ({
          value: waypoint.id,
          label: `After ${waypoint.runningName}`,
        })),
      ], { key: 'route:add-after', tip: FIELD_HINTS.insertPosition }),
      labelledSelect('kind', 'Type', 'major', [
        { value: 'major', label: 'Major waypoint' },
        { value: 'minor', label: 'Minor waypoint', disabled: snapshot.route.length === 0 },
      ], { key: 'route:add-kind', tip: FIELD_HINTS.waypointType }),
      labelledInput('x', 'Horizontal position (%)', 50, {
        ...IMAGE_POSITION_PERCENT, step: 'any', key: 'route:add-x', tip: FIELD_HINTS.newWaypointX,
      }),
      labelledInput('y', 'Vertical position (%)', 50, {
        ...IMAGE_POSITION_PERCENT, step: 'any', key: 'route:add-y', tip: FIELD_HINTS.newWaypointY,
      }),
    ], 'Add waypoint', 'route:add-submit'));

    if (snapshot.route.length === 0) {
      content.appendChild(el('p', { className: 'scene-outline-empty', text: 'No waypoints yet.' }));
    } else {
      const list = el('ol', { className: 'scene-outline-list' });
      for (const waypoint of snapshot.route) list.appendChild(this._renderWaypoint(waypoint));
      content.appendChild(list);
    }
    group.appendChild(content);
    return group;
  }

  _renderWaypoint(waypoint) {
    const selectedWithin = waypointContainsKey(waypoint, this._snapshot.selectionKey);
    const details = detailsBlock(
      waypoint.key,
      `${waypoint.name} — ${waypoint.xLabel}%, ${waypoint.yLabel}%`,
      this._isOpen(waypoint.key, false, selectedWithin),
      waypoint.isMajor ? 'scene-outline-major' : 'scene-outline-minor'
    );
    if (!details.open) return itemFor(details);
    const content = el('div', { className: 'scene-outline-content' });
    const actions = el('div', { className: 'scene-outline-actions' });
    actions.append(
      this._selectButton(`Select ${waypoint.runningName}`, 'waypoint', waypoint.key, { waypointId: waypoint.id }),
      button('Delete waypoint', 'delete-waypoint', { waypointId: waypoint.id }, {
        danger: true,
        key: `${waypoint.key}:delete`,
      })
    );
    content.appendChild(actions);

    const fields = [
      // The heading's two decimals; an untouched field submits the exact value (UI-06 W-02).
      labelledInput('x', 'Horizontal position (%)', waypoint.xLabel, {
        ...IMAGE_POSITION_PERCENT, step: 'any', key: `${waypoint.key}:x`, canonicalValue: waypoint.xCanonical,
        tip: FIELD_HINTS.imageX,
      }),
      labelledInput('y', 'Vertical position (%)', waypoint.yLabel, {
        ...IMAGE_POSITION_PERCENT, step: 'any', key: `${waypoint.key}:y`, canonicalValue: waypoint.yCanonical,
        tip: FIELD_HINTS.imageY,
      }),
    ];
    if (waypoint.isMajor) {
      fields.push(
        labelledInput('waitSeconds', 'Wait (seconds)', waypoint.pauseSeconds, {
          min: 0, max: 600, step: 'any', key: `${waypoint.key}:wait`, canonicalValue: waypoint.pauseMsCanonical,
          tip: FIELD_HINTS.wait,
        }),
        labelledInput('segmentSpeed', 'Leg speed (×)', waypoint.segmentSpeed, {
          min: 0.1, max: 10, step: 'any', key: `${waypoint.key}:speed`, tip: FIELD_HINTS.legSpeed,
        })
      );
    } else {
      content.appendChild(description(
        'Minor waypoints only shape the route; they have no wait or leg speed of their own.'
      ));
    }
    content.appendChild(form(
      'update-waypoint',
      { waypointId: waypoint.id },
      fields,
      'Apply waypoint',
      `${waypoint.key}:apply`
    ));
    content.appendChild(this._renderArea(waypoint));
    details.appendChild(content);
    return itemFor(details);
  }

  _renderArea(waypoint) {
    const area = waypoint.area;
    const wrapper = el('div', { className: 'scene-outline-subgroup' });
    if (area.shape !== 'polygon') {
      if (area.shape === 'none') {
        // It adds a triangle round the waypoint at once (UI-06 low items: its words say so).
        wrapper.appendChild(button('Add triangle area', 'create-polygon', { waypointId: waypoint.id }, {
          key: `${area.key}:create`,
        }));
      } else {
        wrapper.appendChild(description(
          `Area highlight: ${area.shape}. Its appearance remains in the waypoint inspector.`
        ));
      }
      return wrapper;
    }

    const details = detailsBlock(
      area.key,
      // The Area card's words: a drawn area, and its corners (UI-06 low items, one word per concept).
      `Drawn area — ${plural(area.points.length, 'corner', 'corners')}`,
      this._isOpen(area.key, false, areaContainsKey(area, this._snapshot.selectionKey)),
      'scene-outline-polygon'
    );
    if (!details.open) {
      wrapper.appendChild(details);
      return wrapper;
    }
    const content = el('div', { className: 'scene-outline-content' });
    const vertexDraftContext = JSON.stringify(
      area.points.map(point => [point.xCanonical, point.yCanonical])
    );
    const actions = el('div', { className: 'scene-outline-actions' });
    actions.append(
      this._selectButton('Select area', 'polygon', area.key, { waypointId: waypoint.id }),
      button('Delete area', 'delete-polygon', { waypointId: waypoint.id }, {
        danger: true,
        key: `${area.key}:delete`,
      })
    );
    content.appendChild(actions);
    content.appendChild(form('update-polygon-timing', { waypointId: waypoint.id }, [
      labelledInput('fadeInSeconds', 'Fade in (seconds)', area.fadeInSeconds, {
        min: 0, max: 600, step: 'any', key: `${area.key}:fade-in`, canonicalValue: area.fadeInMsCanonical,
        tip: FIELD_HINTS.fadeIn,
      }),
      labelledInput('fadeOutSeconds', 'Fade out (seconds)', area.fadeOutSeconds, {
        min: 0, max: 600, step: 'any', key: `${area.key}:fade-out`, canonicalValue: area.fadeOutMsCanonical,
        tip: FIELD_HINTS.fadeOut,
      }),
    ], 'Apply area timing', `${area.key}:timing`));

    const list = el('ol', { className: 'scene-outline-list scene-outline-point-list' });
    for (const point of area.points) {
      const pointDetails = detailsBlock(
        point.key,
        `Corner ${point.index + 1} — ${point.xLabel}%, ${point.yLabel}%`,
        this._isOpen(point.key, false, this._selected(point.key)),
        'scene-outline-point'
      );
      if (!pointDetails.open) {
        list.appendChild(itemFor(pointDetails));
        continue;
      }
      const pointContent = el('div', { className: 'scene-outline-content' });
      pointContent.appendChild(this._selectButton(`Select corner ${point.index + 1}`, 'vertex', point.key, {
        waypointId: waypoint.id,
        index: point.index,
      }));
      pointContent.appendChild(form('update-vertex', { waypointId: waypoint.id, index: point.index }, [
        labelledInput('x', 'Horizontal position (%)', point.xLabel, {
          ...IMAGE_POSITION_PERCENT, step: 'any', key: `${point.key}:x`, canonicalValue: point.xCanonical,
          tip: FIELD_HINTS.imageX,
        }),
        labelledInput('y', 'Vertical position (%)', point.yLabel, {
          ...IMAGE_POSITION_PERCENT, step: 'any', key: `${point.key}:y`, canonicalValue: point.yCanonical,
          tip: FIELD_HINTS.imageY,
        }),
      ], 'Apply corner', `${point.key}:apply`, vertexDraftContext));
      pointContent.appendChild(button('Delete corner', 'delete-vertex', {
        waypointId: waypoint.id,
        index: point.index,
      }, {
        danger: true,
        disabled: area.points.length <= 3,
        key: `${point.key}:delete`,
      }));
      if (area.points.length <= 3) {
        pointContent.appendChild(description('A drawn area needs at least three corners. Delete the area instead.'));
      }
      pointDetails.appendChild(pointContent);
      list.appendChild(itemFor(pointDetails));
    }
    content.appendChild(list);
    content.appendChild(form('add-vertex', { waypointId: waypoint.id }, [
      labelledInput('x', 'New corner horizontal position (%)', waypoint.xLabel, {
        ...IMAGE_POSITION_PERCENT, step: 'any', key: `${area.key}:add-x`, tip: FIELD_HINTS.newVertexX,
      }),
      labelledInput('y', 'New corner vertical position (%)', waypoint.yLabel, {
        ...IMAGE_POSITION_PERCENT, step: 'any', key: `${area.key}:add-y`, tip: FIELD_HINTS.newVertexY,
      }),
    ], 'Add corner', `${area.key}:add`));
    details.appendChild(content);
    wrapper.appendChild(details);
    return wrapper;
  }

  _renderCrowds(snapshot) {
    const selectedCrowd = snapshot.crowds.some(crowd => crowdContainsKey(crowd, snapshot.selectionKey));
    const group = detailsBlock(
      'crowds',
      `Crowds — ${plural(snapshot.crowds.length, 'layer')}`,
      this._isOpen('crowds', true, selectedCrowd),
      'scene-outline-group'
    );
    if (!group.open) return group;
    const content = el('div', { className: 'scene-outline-content' });
    content.appendChild(description(
      'Each crowd releases dots that follow the route or the crowd’s own custom network.'
    ));
    content.appendChild(button('Add crowd', 'add-crowd', {}, { key: 'crowds:add' }));
    if (snapshot.crowds.length === 0) {
      content.appendChild(el('p', { className: 'scene-outline-empty', text: 'No crowds yet.' }));
    } else {
      const list = el('ol', { className: 'scene-outline-list' });
      snapshot.crowds.forEach(crowd => list.appendChild(this._renderCrowd(crowd)));
      content.appendChild(list);
    }
    group.appendChild(content);
    return group;
  }

  _renderCrowd(crowd) {
    const selectedWithin = crowdContainsKey(crowd, this._snapshot.selectionKey);
    const guide = crowd.guideType === 'route' ? 'Follow route' : 'Custom network';
    const details = detailsBlock(
      crowd.key,
      `${crowd.displayName} — ${guide}, ${plural(crowd.emitters.length, 'emitter')}${crowd.visible ? '' : ', hidden'}`,
      this._isOpen(crowd.key, false, selectedWithin),
      'scene-outline-crowd'
    );
    if (!details.open) return itemFor(details);
    const content = el('div', { className: 'scene-outline-content' });
    const actions = el('div', { className: 'scene-outline-actions' });
    actions.append(
      this._selectButton(`Select ${crowd.displayName}`, 'crowd', crowd.key, { layerId: crowd.id }),
      button('Delete crowd', 'delete-crowd', { layerId: crowd.id }, {
        danger: true,
        key: `${crowd.key}:delete`,
      })
    );
    content.appendChild(actions);
    const duplicate = form('duplicate-crowd', { layerId: crowd.id, duplicateContext: crowd.duplicateContext },
      [], 'Duplicate crowd', `${crowd.key}:duplicate`, crowd.duplicateContext);
    const submit = duplicate.querySelector('[type="submit"]');
    submit.disabled = crowd.duplicateBusy;
    submit.setAttribute('aria-busy', String(crowd.duplicateBusy));
    submit.setAttribute('data-tip', 'Copy this crowd above it in drawing order, with a new pattern.');
    if (crowd.duplicateBusy) duplicate.appendChild(description('Checking the project before duplicating…'));
    content.appendChild(duplicate);
    content.appendChild(form('update-crowd', { layerId: crowd.id }, [
      labelledInput('name', 'Crowd name', crowd.name, {
        type: 'text', required: false, inputMode: null, maxLength: 200,
        key: `${crowd.key}:name`, canonicalValue: crowd.name, tip: FIELD_HINTS.crowdName,
      }),
      labelledSelect('visible', 'Visibility', crowd.visible ? 'shown' : 'hidden', [
        { value: 'shown', label: 'Shown' },
        { value: 'hidden', label: 'Hidden' },
      ], { key: `${crowd.key}:visible`, tip: FIELD_HINTS.crowdVisibility }),
      labelledSelect('guideType', 'Guide', crowd.guideType, [
        { value: 'route', label: 'Follow route' },
        { value: 'graph', label: 'Custom network' },
      ], { key: `${crowd.key}:guide`, tip: FIELD_HINTS.guide }),
    ], 'Apply crowd', `${crowd.key}:apply`));

    const emittersKey = sceneOutlineKey('emitters', crowd.id);
    const emitters = detailsBlock(
      emittersKey,
      `Emitters — ${crowd.emitters.length}`,
      this._isOpen(
        emittersKey,
        false,
        crowd.emitters.some(emitter => this._snapshot.selectionKey === emitter.key)
      ),
      'scene-outline-subgroup'
    );
    const emitterList = el('ol', { className: 'scene-outline-list' });
    if (emitters.open) {
      crowd.emitters.forEach(emitter => emitterList.appendChild(this._renderEmitter(crowd, emitter)));
      if (crowd.emitters.length === 0) {
        emitters.appendChild(el('p', {
          className: 'scene-outline-empty',
          text: 'This crowd has no emitters.',
        }));
      } else {
        emitters.appendChild(emitterList);
      }
    }
    content.appendChild(emitters);
    content.appendChild(this._renderNetwork(crowd));
    details.appendChild(content);
    return itemFor(details);
  }

  _renderEmitter(crowd, emitter) {
    const details = detailsBlock(
      emitter.key,
      `Emitter ${emitter.index + 1}${emitter.primary ? ' — primary' : ''}, ${plural(emitter.dotCount, 'dot')}`,
      this._isOpen(emitter.key, false, this._selected(emitter.key)),
      'scene-outline-emitter'
    );
    if (!details.open) return itemFor(details);
    const content = el('div', { className: 'scene-outline-content' });
    content.appendChild(this._selectButton(`Select emitter ${emitter.index + 1}`, 'emitter', emitter.key, {
      layerId: crowd.id,
      emitterId: emitter.id,
    }));
    if (!emitter.primary) {
      content.appendChild(description(
        'These existing controls are read-only: editing more than one emitter in a crowd is a later feature. ' +
        'Groups settings can be edited separately below.'
      ));
      content.appendChild(readOnlyList([
        ['Count', emitter.dotCount],
        ['Window start', `${emitter.releaseStart}%`],
        ['Window length', `${emitter.releaseDuration}%`],
        ['Speed', `${emitter.speed} image lengths/s`],
        ['Pace variation', `${emitter.speedVariance}%`],
        ['Release timing', `${emitter.onsetVariance}%`],
        ['Release bias', releaseBiasText(emitter.intensityRamp)],
        ['Busyness', `${emitter.busynessEnvelope.length} handles`],
        ['Walking variation', `${emitter.wobble}%`],
        ['At journey end', JOURNEY_END_LABELS[emitter.lifecycleMode] ?? emitter.lifecycleMode],
        ['Pattern seed', emitter.seed],
      ]));
    } else {
      content.appendChild(form('update-emitter', { layerId: crowd.id, emitterId: emitter.id }, [
        labelledInput('dotCount', 'Count', emitter.dotCount, {
          min: 1, max: 5000, step: 1, inputMode: 'numeric', key: `${emitter.key}:count`, tip: FIELD_HINTS.dots,
        }),
        labelledInput('releaseStart', 'Window start (%)', emitter.releaseStart, {
          min: 0, max: 100, step: 'any', key: `${emitter.key}:start`, canonicalValue: emitter.releaseStartCanonical,
          tip: emitter.waitingActive ? WAITING_RELEASE_HINTS.start
            : emitter.groupsActive ? groupChoiceHint('group', FIELD_HINTS.releaseStart)
            + ' on the project reference timeline' : FIELD_HINTS.releaseStart,
        }),
        labelledInput('releaseDuration', 'Window length (%)', emitter.releaseDuration, {
          min: 0,
          max: 100,
          step: 'any',
          key: `${emitter.key}:duration`,
          canonicalValue: emitter.releaseDurationCanonical,
          tip: emitter.waitingActive ? WAITING_RELEASE_HINTS.length
            : emitter.groupsActive ? groupChoiceHint('group', FIELD_HINTS.releaseLength)
            + ' on the project reference timeline' : FIELD_HINTS.releaseLength,
        }),
        labelledInput('onsetVariance', 'Release timing (%)', emitter.onsetVariance, {
          min: 0, max: 100, step: 'any', key: `${emitter.key}:onset`, canonicalValue: emitter.onsetVarianceCanonical,
          tip: emitter.waitingActive ? WAITING_RELEASE_HINTS.timing
            : groupChoiceHint(emitter.groupsActive ? 'group' : null, FIELD_HINTS.releaseTiming),
        }),
        ...releaseBiasFields(emitter),
        labelledInput('speed', 'Speed (image lengths/s)', emitter.speed, {
          min: 0.001, max: 1000, step: 'any', key: `${emitter.key}:speed`,
          tip: groupChoiceHint(emitter.groupsActive ? 'group' : null, FIELD_HINTS.speed),
        }),
        labelledInput('speedVariance', 'Pace variation (%)', emitter.speedVariance, {
          min: 0,
          max: 100,
          step: 'any',
          key: `${emitter.key}:speed-variance`,
          canonicalValue: emitter.speedVarianceCanonical,
          tip: groupChoiceHint(emitter.groupsActive ? 'group' : null, FIELD_HINTS.paceVariation),
        }),
        labelledInput('dotSize', 'Dot size (×)', emitter.dotSize, {
          min: 0.01, max: 100, step: 'any', key: `${emitter.key}:size`, tip: dotSizeHint(emitter.dotSize),
        }),
        labelledInput('wobble', 'Walking variation (%)', emitter.wobble, {
          min: 0, max: 100, step: 'any', key: `${emitter.key}:wobble`, canonicalValue: emitter.wobbleCanonical,
          tip: FIELD_HINTS.walkingVariation,
        }),
        labelledInput('dotColor', 'Dot colour (hex or transparent)', emitter.dotColor, {
          type: 'text', inputMode: null, maxLength: 11, key: `${emitter.key}:color`, tip: FIELD_HINTS.dotColour,
        }),
        labelledSelect('lifecycleMode', 'At journey end', emitter.lifecycleMode, [
          { value: 'disappear', label: JOURNEY_END_LABELS.disappear },
          { value: 'respawn', label: JOURNEY_END_LABELS.respawn },
          { value: 'loop', label: JOURNEY_END_LABELS.loop },
          { value: 'collect', label: JOURNEY_END_LABELS.collect },
        ], { key: `${emitter.key}:lifecycle`, tip: FIELD_HINTS.journeyEnd }),
      ], 'Apply crowd', `${emitter.key}:apply`));
      content.appendChild(description(
        `Busyness over time has ${emitter.busynessEnvelope.length} handles. ` +
        'Select this crowd in the main editor to move handles or set gradual and sudden spans.'
      ));
      content.appendChild(readOnlyList([['Pattern seed', emitter.seed]]));
    }
    content.appendChild(this._renderGroupForm(crowd, emitter));
    content.appendChild(this._renderWaitingForm(crowd, emitter));
    details.appendChild(content);
    return itemFor(details);
  }

  _renderWaitingForm(crowd, emitter) {
    if (emitter.waitingContext == null) return readOnlyList([
      ['Final whistle', emitter.waitAtStart ? 'On' : 'Off'],
    ]);
    const key = `${emitter.key}:waiting-apply`, reason = emitter.waitingReason;
    const draft = this._drafts.get(key);
    const pendingOn = draft?.context === emitter.waitingContext
      && [...draft.fields.values()].some(field => field.name === 'waitAtStart' && field.checked);
    const ownError = this._error?.formKey === key && this._error.context === emitter.waitingContext;
    const disabled = !!reason && !emitter.waitAtStart && !pendingOn && !ownError;
    const check = el('input', { attrs: { type: 'checkbox', name: 'waitAtStart' },
      data: { outlineKey: `${emitter.key}:waiting` } });
    check.checked = emitter.waitAtStart; check.defaultChecked = emitter.waitAtStart; check.disabled = disabled;
    const label = el('label', { className: 'checkbox-label crowd-wait-toggle' });
    label.append(check, el('span', { text: 'Final whistle', attrs: { 'data-tip': WAITING_FIELD_HINT } }));
    const node = form('update-waiting', { layerId: crowd.id, emitterId: emitter.id,
      waitingContext: emitter.waitingContext }, [label], 'Apply final whistle', key, emitter.waitingContext);
    node.classList.add('crowd-wait-form'); node.querySelector('[type="submit"]').disabled = disabled;
    const help = el('p', { className: 'scene-outline-hint', text: WAITING_HINT,
      attrs: { id: `${OUTLINE_REASON_ID_PREFIX}${++outlineReasonSerial}` } });
    const status = el('p', { className: 'scene-outline-hint', text: reason
      ? `${emitter.waitAtStart ? 'Stored on. ' : pendingOn ? 'Unapplied on. ' : ''}` + reason
        + (emitter.waitAtStart || pendingOn ? ' Turn off or use Cancel edits to restore the stored setting.' : '') : '',
    attrs: { id: `${OUTLINE_REASON_ID_PREFIX}${++outlineReasonSerial}` } });
    status.hidden = !status.textContent; node.append(help, status);
    check.setAttribute('aria-describedby', `${help.id} ${status.id}`);
    node.appendChild(button('Cancel edits', 'cancel-waiting', {}, { key: `${emitter.key}:waiting-cancel` }));
    return node;
  }

  /** Waiting tokens survive prerequisite edits; stale model tokens do not. */
  pendingWaitingDrafts(contexts, exemption = null) {
    this._captureDirtyDrafts();
    const pending = [];
    for (const [key, draft] of this._drafts) {
      if (!key.endsWith(':waiting-apply')) continue;
      const current = contexts.get(key);
      if (!current || draft.context !== current.context) { this._drafts.delete(key); continue; }
      if (exemption && (key !== exemption.formKey || exemption.surface === 'outline')) continue;
      if ([...draft.fields.values()].some(field => field.name === 'waitAtStart'
          && field.checked !== current.waitAtStart)) pending.push(key);
    }
    return pending;
  }

  _renderGroupForm(crowd, emitter) {
    // A semantic view without an app-owned identity token can describe settings,
    // but cannot submit a checked command against a live emitter.
    if (emitter.groupsContext == null) return readOnlyList([
      ['Groups', emitter.groups ? 'On' : 'Off'], ['Group size (dots)', emitter.groupSize],
      ['Group width (%)', emitter.groupWidth],
    ]);
    const key = `${emitter.key}:groups-apply`, reason = emitter.groupsReason;
    const check = el('input', { attrs: { type: 'checkbox', name: 'groups' },
      data: { outlineKey: `${emitter.key}:groups` } });
    check.checked = emitter.groups; check.defaultChecked = emitter.groups;
    check.disabled = !!reason && !emitter.groups;
    const label = el('label', { className: 'checkbox-label crowd-group-toggle' });
    label.append(check, el('span', { text: 'Groups', attrs: { 'data-tip': GROUP_FIELD_HINTS.groups } }));
    const node = form('update-groups', { layerId: crowd.id, emitterId: emitter.id,
      groupsContext: emitter.groupsContext }, [label,
      labelledInput('groupSize', 'Group size (dots)', emitter.groupSize,
        { min: 1, max: 5000, step: 1, key: `${emitter.key}:group-size`, tip: GROUP_FIELD_HINTS.groupSize }),
      labelledInput('groupWidth', 'Group width (%)', emitter.groupWidth,
        { min: 0, max: 20, step: 'any', key: `${emitter.key}:group-width`,
          canonicalValue: emitter.groupWidthCanonical, tip: GROUP_FIELD_HINTS.groupWidth }),
    ], 'Apply groups', key, emitter.groupsContext);
    node.elements.groupSize.disabled = !!reason; node.elements.groupWidth.disabled = !!reason;
    node.querySelector('[type="submit"]').disabled = !!reason && !emitter.groups;
    const help = el('p', { className: 'scene-outline-hint',
      text: reason ? `${emitter.groups ? 'Stored on; turn off, or ' : ''}${reason}`
        : GROUP_HINT,
      attrs: { id: `${OUTLINE_REASON_ID_PREFIX}${++outlineReasonSerial}` } });
    node.appendChild(help);
    for (const field of node.elements) field.setAttribute('aria-describedby', help.id);
    node.appendChild(button('Cancel edits', 'cancel-groups', {}, { key: `${emitter.key}:groups-cancel` }));
    return node;
  }

  /** Includes retained hidden forms, but only while their live identity/context is current. */
  pendingGroupDrafts(contexts, exemption = null) {
    this._captureDirtyDrafts();
    const pending = [];
    for (const [key, draft] of this._drafts) {
      if (!key.endsWith(':groups-apply')) continue;
      const current = contexts.get(key);
      if (!current || draft.context !== current.context) { this._drafts.delete(key); continue; }
      if (exemption && (key !== exemption.formKey || exemption.surface === 'outline')) continue;
      const dirty = [...draft.fields.values()].some(field => {
        if (field.name === 'groups') return field.checked !== current.groups;
        if (field.name === 'groupSize') return !field.value.trim() || Number(field.value) !== current.groupSize;
        if (field.name === 'groupWidth') {
          return !field.value.trim() || shiftDecimal(Number(field.value), -2) !== current.groupWidth;
        }
        return false;
      });
      if (dirty) pending.push(key);
    }
    return pending;
  }

  cancelGroupDraft(key) {
    this._drafts.delete(key);
    const formEl = this._formForKey(key);
    formEl?.reset(); this._clearEdits(formEl);
    if (this._error?.formKey === key) this._clearCommandError();
  }

  _renderNetwork(crowd) {
    const graph = crowd.graph;
    const label = graph.active
      ? 'Custom network'
      : 'Custom network — not in use while this crowd follows the route';
    const selectedWithin = networkContainsKey(graph, this._snapshot.selectionKey);
    const details = detailsBlock(
      graph.key,
      `${label} — ${plural(graph.nodes.length, 'node')}, ${plural(graph.edges.length, 'path')}`,
      this._isOpen(graph.key, false, selectedWithin),
      'scene-outline-network'
    );
    if (!details.open) return details;
    const content = el('div', { className: 'scene-outline-content' });
    content.appendChild(description(graph.active
      ? 'This network guides the crowd. Add nodes, then connect them with paths.'
      : 'These paths are kept but not drawn until Guide is set to Custom network.'));
    content.appendChild(form('add-node', { layerId: crowd.id }, [
      labelledInput('x', 'Node horizontal position (%)', 50, {
        min: 0, max: 100, step: 'any', key: `${graph.key}:add-node-x`, tip: FIELD_HINTS.newNodeX,
      }),
      labelledInput('y', 'Node vertical position (%)', 50, {
        min: 0, max: 100, step: 'any', key: `${graph.key}:add-node-y`, tip: FIELD_HINTS.newNodeY,
      }),
      labelledSelect('type', 'Node type', 'normal', [
        { value: 'normal', label: 'Pass-through' },
        { value: 'entry', label: 'Entry' },
        { value: 'exit', label: 'Exit' },
      ], { key: `${graph.key}:add-node-type`, tip: FIELD_HINTS.nodeType }),
      labelledInput('label', 'Node label (optional)', '', {
        type: 'text', required: false, inputMode: null, maxLength: 200, key: `${graph.key}:add-node-label`,
        tip: FIELD_HINTS.nodeLabel,
      }),
    ], 'Add node', `${graph.key}:add-node`));

    if (graph.nodes.length >= 2) {
      const nodeOptions = graph.nodes.map(node => ({
        value: node.id,
        label: `${node.name} — ${node.xLabel}%, ${node.yLabel}%`,
      }));
      content.appendChild(form('connect-nodes', { layerId: crowd.id }, [
        labelledSelect('sourceId', 'Source node', graph.nodes[0].id, nodeOptions, {
          key: `${graph.key}:source`, tip: FIELD_HINTS.sourceNode,
        }),
        labelledSelect('targetId', 'Destination node', graph.nodes[1].id, nodeOptions, {
          key: `${graph.key}:target`, tip: FIELD_HINTS.destinationNode,
        }),
        labelledSelect('direction', 'Direction', 'two-way', [
          { value: 'two-way', label: 'Two-way' },
          { value: 'one-way', label: 'One-way' },
        ], { key: `${graph.key}:direction`, tip: FIELD_HINTS.newEdgeDirection }),
        labelledInput('weight', 'Path weight', 1, {
          min: 0.01, step: 'any', key: `${graph.key}:weight`,
          tip: groupChoiceHint(crowd.choiceRole, FIELD_HINTS.pathWeight),
        }),
      ], 'Connect nodes', `${graph.key}:connect`));
    } else {
      content.appendChild(description('Add at least two nodes before connecting a path.'));
    }

    const nodesKey = sceneOutlineKey('nodes', crowd.id);
    const nodes = detailsBlock(
      nodesKey,
      `Nodes — ${graph.nodes.length}`,
      this._isOpen(
        nodesKey,
        false,
        graph.nodes.some(node => this._snapshot.selectionKey === node.key)
      ),
      'scene-outline-subgroup'
    );
    const nodeList = el('ol', { className: 'scene-outline-list' });
    if (nodes.open) {
      graph.nodes.forEach(node => nodeList.appendChild(this._renderNode(crowd, node)));
      nodes.appendChild(graph.nodes.length
        ? nodeList
        : el('p', { className: 'scene-outline-empty', text: 'No nodes.' }));
    }
    content.appendChild(nodes);

    const edgesKey = sceneOutlineKey('edges', crowd.id);
    const edges = detailsBlock(
      edgesKey,
      `Paths — ${graph.edges.length}`,
      this._isOpen(
        edgesKey,
        false,
        graph.edges.some(edge => edgeContainsKey(edge, this._snapshot.selectionKey))
      ),
      'scene-outline-subgroup'
    );
    const edgeList = el('ol', { className: 'scene-outline-list' });
    if (edges.open) {
      graph.edges.forEach(edge => edgeList.appendChild(this._renderEdge(crowd, edge)));
      edges.appendChild(graph.edges.length
        ? edgeList
        : el('p', { className: 'scene-outline-empty', text: 'No paths.' }));
    }
    content.appendChild(edges);
    details.appendChild(content);
    return details;
  }

  _renderNode(crowd, node) {
    const details = detailsBlock(
      node.key,
      `${node.name} — ${node.xLabel}%, ${node.yLabel}%`,
      this._isOpen(node.key, false, this._selected(node.key)),
      'scene-outline-node'
    );
    if (!details.open) return itemFor(details);
    const content = el('div', { className: 'scene-outline-content' });
    content.appendChild(this._selectButton(`Select node ${node.index + 1}`, 'node', node.key, {
      layerId: crowd.id,
      nodeId: node.id,
    }));
    content.appendChild(form('update-node', { layerId: crowd.id, nodeId: node.id }, [
      labelledInput('x', 'Horizontal position (%)', node.xLabel, {
        min: 0, max: 100, step: 'any', key: `${node.key}:x`, canonicalValue: node.xCanonical,
        tip: FIELD_HINTS.onImageX,
      }),
      labelledInput('y', 'Vertical position (%)', node.yLabel, {
        min: 0, max: 100, step: 'any', key: `${node.key}:y`, canonicalValue: node.yCanonical,
        tip: FIELD_HINTS.onImageY,
      }),
      labelledSelect('type', 'Type', node.type, [
        { value: 'normal', label: 'Pass-through' },
        { value: 'entry', label: 'Entry' },
        { value: 'exit', label: 'Exit' },
      ], { key: `${node.key}:type`, tip: FIELD_HINTS.nodeType }),
      labelledInput('label', 'Label (optional)', node.label, {
        type: 'text', required: false, inputMode: null, maxLength: 200,
        key: `${node.key}:label`, canonicalValue: node.label, tip: FIELD_HINTS.nodeLabel,
      }),
    ], 'Apply node', `${node.key}:apply`));
    if (['entry', 'exit'].includes(node.type) || node.area) content.appendChild(this._renderNodeArea(crowd, node));
    if (node.type === 'entry' || node.entryWeight !== undefined) {
      content.appendChild(this._renderNodeEntryWeight(crowd, node));
    }
    content.appendChild(button(
      `Delete node${node.connectedEdges ? ` and ${plural(node.connectedEdges, 'connected path')}` : ''}`,
      'delete-node',
      { layerId: crowd.id, nodeId: node.id },
      { danger: true, key: `${node.key}:delete` }
    ));
    details.appendChild(content);
    return itemFor(details);
  }

  _renderNodeEntryWeight(crowd, node) {
    const key = `${node.key}:entry-weight-apply`;
    const active = crowd.graph.active && node.type === 'entry';
    const formEl = form('update-entry-weight', {
      layerId: crowd.id, nodeId: node.id, entryWeightContext: node.entryWeightContext,
    }, [labelledInput('entryWeight', 'Entry weight', node.entryWeight ?? CROWD_ENTRY_WEIGHT.DEFAULT, {
      min: CROWD_ENTRY_WEIGHT.MIN, max: CROWD_ENTRY_WEIGHT.MAX, step: 'any', key: `${node.key}:entry-weight`,
      tip: entryWeightHint(crowd.choiceRole),
    })], 'Apply entry weight', key, node.entryWeightContext);
    const hint = el('p', { className: 'scene-outline-hint',
      text: !crowd.graph.active ? 'Choose Custom network in Guide to use this retained weight.'
        : node.type === 'entry' ? entryWeightHint(crowd.choiceRole)
          : 'Choose Entry in Type to use this retained weight.',
      attrs: { id: `${OUTLINE_REASON_ID_PREFIX}${++outlineReasonSerial}` } });
    const input = formEl.elements.entryWeight;
    const output = el('output', { className: 'scene-outline-hint',
      text: active ? `${node.entryPercent}% of ${crowd.choiceRole ? 'Entry choices' : 'dots'}`
        : 'Entry weight inactive',
      attrs: { id: `${hint.id}-share` } });
    input.setAttribute('aria-describedby', `${output.id} ${hint.id}`);
    input.disabled = !active;
    formEl.querySelector('[type="submit"]').disabled = input.disabled;
    input.addEventListener('invalid', event => {
      event.preventDefault();
      try { entryWeightValue(input.value.trim() ? Number(input.value) : NaN); }
      catch (failure) { this._showCommandError({ formKey: key, message: failure.message }); }
    });
    formEl.append(output, hint);
    return formEl;
  }

  _renderNodeArea(crowd, node) {
    const area = node.area || createCrowdArea();
    const description = node.areaDescription
      || crowdAreaDescription({ type: node.type, hasImage: node.hasBackgroundImage });
    const moreKey = `${node.key}:area-more`;
    const more = detailsBlock(moreKey, 'More', this._isOpen(moreKey), 'section-more');
    more.appendChild(labelledInput('turn', 'Turn (°)', area.turn, {
      min: -180, max: 180, step: 'any', key: `${node.key}:area-turn`, canonicalValue: area.turn,
      tip: 'Clockwise turn in the image’s plane; a negative value turns anticlockwise',
    }));
    const formEl = form('update-node-area', {
      layerId: crowd.id, nodeId: node.id, areaInactive: !['entry', 'exit'].includes(node.type),
    }, [
      labelledSelect('shape', description.label, node.area?.shape || 'none', [
        { value: 'none', label: 'None' }, { value: 'rectangle', label: 'Rectangle' },
        { value: 'ellipse', label: 'Ellipse' },
      ], { key: `${node.key}:area-shape`,
        tip: 'Centred on this node. Entry dots walk to the node; collecting Exit dots walk into places' }),
      ...['width', 'height'].map(key => labelledInput(key,
        key === 'width' ? 'Width (%)' : 'Height (%)', area[key] * 100, {
        min: 1, max: 80, step: 'any', key: `${node.key}:area-${key}`, canonicalValue: area[key],
        tip: `${key === 'width' ? 'Width' : 'Height'} before turning, as a percentage of the image’s ${key}`,
      })),
      more,
    ], `Apply ${description.label.toLowerCase()}`, `${node.key}:area-apply`, JSON.stringify([node.type, node.area]));
    const hints = [description.hint, description.reason, description.imageHint].filter(Boolean);
    const hint = el('p', { className: 'scene-outline-hint', text: hints.join(' '),
      attrs: { id: `${OUTLINE_REASON_ID_PREFIX}${++outlineReasonSerial}` } });
    formEl.appendChild(hint);
    for (const field of formEl.querySelectorAll('input, select')) field.setAttribute('aria-describedby', hint.id);
    return formEl;
  }

  _renderEdge(crowd, edge) {
    const details = detailsBlock(
      edge.key,
      `Path ${edge.index + 1} — ${edge.sourceName} to ${edge.targetName}, ${edge.direction}`,
      this._isOpen(edge.key, false, edgeContainsKey(edge, this._snapshot.selectionKey)),
      'scene-outline-edge'
    );
    if (!details.open) return itemFor(details);
    const content = el('div', { className: 'scene-outline-content' });
    content.appendChild(this._selectButton(`Select path ${edge.index + 1}`, 'edge', edge.key, {
      layerId: crowd.id,
      edgeId: edge.id,
    }));
    content.appendChild(form('update-edge', { layerId: crowd.id, edgeId: edge.id }, [
      labelledSelect('direction', 'Direction', edge.direction, [
        { value: 'two-way', label: 'Two-way' },
        { value: 'one-way', label: 'One-way' },
      ], { key: `${edge.key}:direction`, tip: FIELD_HINTS.edgeDirection }),
      labelledInput('weight', 'Path weight', edge.weight, {
        min: 0.01, step: 'any', key: `${edge.key}:weight`, canonicalValue: edge.weight,
        tip: groupChoiceHint(crowd.choiceRole, FIELD_HINTS.pathWeight),
      }),
    ], 'Apply path', `${edge.key}:apply`));
    content.appendChild(button('Delete path', 'delete-edge', {
      layerId: crowd.id,
      edgeId: edge.id,
    }, { danger: true, key: `${edge.key}:delete` }));

    const controlsKey = sceneOutlineKey('controls', crowd.id, edge.id);
    const points = detailsBlock(
      controlsKey,
      `Bends — ${edge.controlPoints.length}`,
      this._isOpen(
        controlsKey,
        false,
        edge.controlPoints.some(point => this._snapshot.selectionKey === point.key)
      ),
      'scene-outline-subgroup'
    );
    const controlDraftContext = JSON.stringify(
      edge.controlPoints.map(point => [point.xCanonical, point.yCanonical])
    );
    const list = el('ol', { className: 'scene-outline-list scene-outline-point-list' });
    for (const point of points.open ? edge.controlPoints : []) {
      const pointDetails = detailsBlock(
        point.key,
        `Bend ${point.index + 1} — ${point.xLabel}%, ${point.yLabel}%`,
        this._isOpen(point.key, false, this._selected(point.key)),
        'scene-outline-point'
      );
      if (!pointDetails.open) {
        list.appendChild(itemFor(pointDetails));
        continue;
      }
      const pointContent = el('div', { className: 'scene-outline-content' });
      pointContent.appendChild(this._selectButton(`Select bend ${point.index + 1}`, 'control', point.key, {
        layerId: crowd.id,
        edgeId: edge.id,
        index: point.index,
      }));
      pointContent.appendChild(form('update-control', {
        layerId: crowd.id,
        edgeId: edge.id,
        index: point.index,
      }, [
        labelledInput('x', 'Horizontal position (%)', point.xLabel, {
          ...bendPercentRange(point.x, point.xLabel), step: 'any', key: `${point.key}:x`,
          canonicalValue: point.xCanonical, tip: FIELD_HINTS.onImageX,
        }),
        labelledInput('y', 'Vertical position (%)', point.yLabel, {
          ...bendPercentRange(point.y, point.yLabel), step: 'any', key: `${point.key}:y`,
          canonicalValue: point.yCanonical, tip: FIELD_HINTS.onImageY,
        }),
      ], 'Apply bend', `${point.key}:apply`, controlDraftContext));
      pointContent.appendChild(button('Delete bend', 'delete-control', {
        layerId: crowd.id,
        edgeId: edge.id,
        index: point.index,
      }, { danger: true, key: `${point.key}:delete` }));
      pointDetails.appendChild(pointContent);
      list.appendChild(itemFor(pointDetails));
    }
    if (points.open) {
      points.appendChild(edge.controlPoints.length
        ? list
        : el('p', {
          className: 'scene-outline-empty',
          text: 'No bends; the path is straight.',
        }));
      points.appendChild(form('add-control', { layerId: crowd.id, edgeId: edge.id }, [
        labelledInput('x', 'New bend horizontal position (%)', 50, {
          min: 0, max: 100, step: 'any', key: `${edge.key}:add-control-x`, tip: FIELD_HINTS.newBendX,
        }),
        labelledInput('y', 'New bend vertical position (%)', 50, {
          min: 0, max: 100, step: 'any', key: `${edge.key}:add-control-y`, tip: FIELD_HINTS.newBendY,
        }),
      ], 'Add bend', `${edge.key}:add-control`));
    }
    content.appendChild(points);
    details.appendChild(content);
    return itemFor(details);
  }

  _onSubmit(event) {
    const formEl = event.target.closest('form[data-outline-action]');
    if (!formEl || !this.container.contains(formEl)) return;
    event.preventDefault();
    if (!formEl.reportValidity()) return;
    let feedbackToken;
    if (formEl.dataset.outlineAction === 'duplicate-crowd') {
      if (formEl.querySelector('[type="submit"]').disabled) return;
      this._clearDuplicateError(formEl.dataset.outlineFormKey);
      feedbackToken = ++this._duplicateFeedbackSerial;
      this._duplicateFeedback.set(formEl.dataset.outlineFormKey,
        { token: feedbackToken, context: formEl[OUTLINE_DRAFT_CONTEXT] });
    } else if (!['update-groups', 'update-waiting'].includes(formEl.dataset.outlineAction)
        || this._error?.formKey === formEl.dataset.outlineFormKey) {
      this._clearCommandError();
    }
    const values = Object.fromEntries(new FormData(formEl).entries());
    const outlineOriginalValues = {};
    for (const field of formEl.elements) {
      if (!field.name || !(OUTLINE_CANONICAL_VALUE in field)) continue;
      // An edited field submits what it holds, whatever it holds (UI-06 W-02, Codex r1).
      if (field[OUTLINE_EDITED]) continue;
      outlineOriginalValues[field.name] = {
        display: field.defaultValue,
        canonical: field[OUTLINE_CANONICAL_VALUE],
      };
    }
    this.eventBus.emit('scene-outline:command', {
      action: formEl.dataset.outlineAction,
      ...formEl.dataset,
      ...values,
      outlineOriginalValues,
      feedbackToken,
    });
  }

  _onClick(event) {
    const summary = event.target.closest('summary[data-outline-key]');
    if (summary && this.container.contains(summary)) {
      // The outline, not the browser, opens and closes its entries (DEF-40).
      // A browser toggles an entry only after the click's microtasks, so a
      // redraw queued here drew the entry as it was, empty, and the toggle
      // then landed on the element the redraw had replaced. Enter and Space
      // on a summary arrive as this same click.
      event.preventDefault();
      const details = summary.parentElement;
      details.open = !details.open;
      if (this._snapshot) this.render(this._snapshot, summary.dataset.outlineKey);
      return;
    }
    const target = event.target.closest('button[data-outline-action]');
    if (!target || !this.container.contains(target)) return;
    if (target.dataset.outlineAction === 'cancel-waiting') {
      const key = target.closest('form')?.dataset.outlineFormKey;
      this.cancelGroupDraft(key);
      if (this._snapshot) this.render(this._snapshot, `${key?.replace(/:waiting-apply$/, '')}:waiting`);
      return;
    }
    if (target.dataset.outlineAction === 'cancel-groups') {
      this.cancelGroupDraft(target.closest('form')?.dataset.outlineFormKey); return;
    }
    this.eventBus.emit('scene-outline:command', {
      action: target.dataset.outlineAction,
      ...target.dataset,
    });
  }

  _onKeyDown(event) {
    if (event.key !== 'Escape') return;
    const field = event.target.closest('input, select, textarea');
    if (!field || !this.container.contains(field)) return;
    event.preventDefault();
    event.stopPropagation();
    if (['update-groups', 'update-waiting'].includes(field.form?.dataset.outlineAction)) {
      const key = field.form.dataset.outlineFormKey;
      this.cancelGroupDraft(key);
      if (field.form.dataset.outlineAction === 'update-waiting' && this._snapshot) this.render(this._snapshot, key);
      this._formForKey(key)?.querySelector('[type="submit"]')?.focus();
      return;
    }
    this._clearCommandError();
    this._drafts.delete(field.form?.dataset?.outlineFormKey);
    field.form?.reset();
    this._clearEdits(field.form);
    syncReleaseBias(field.form);
    syncNodeArea(field.form);
    field.form?.querySelector('[type="submit"]')?.focus();
  }

  /**
   * An explicit edit, typed or chosen, marks its field (UI-06 W-02, Codex r1).
   * The mark is kept through the outline's redraws as a draft is, and goes
   * when the Apply is accepted, on Escape, with a new project, or, for a
   * corner or a bend, when the point list it was drawn from changes; a
   * refused Apply keeps it.
   */
  _onEdit(event) {
    const field = event.target;
    if (!field?.form?.dataset?.outlineFormKey || !this.container.contains(field)) return;
    if (OUTLINE_CANONICAL_VALUE in field) field[OUTLINE_EDITED] = true;
    if (field.name === 'intensityRampDirection') syncReleaseBias(field.form);
    if (field.name === 'shape') syncNodeArea(field.form);
  }
}
